import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BlockSelection from '../components/vehicle-registration/BlockSelection';
import FlatSelection from '../components/vehicle-registration/FlatSelection';
import ResidentInfo from '../components/vehicle-registration/ResidentInfo';
import VehicleTypeSelection from '../components/vehicle-registration/VehicleTypeSelection';
import VehicleDetails from '../components/vehicle-registration/VehicleDetails';
import SecurityAccess from '../components/vehicle-registration/SecurityAccess';
import ReviewSubmit from '../components/vehicle-registration/ReviewSubmit';

const VehicleRegistrationPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    block: '',
    tower: '',
    flatNumber: '',
    residentName: '',
    residentType: 'owner',
    mobileNumber: '',
    email: '',
    vehicleTypes: [],
    vehicles: [],
    rfidTagNumber: '',
    emergencyContact: '',
    parkingSlotNumbers: [],
    remarks: ''
  });

  const updateFormData = useCallback((data) => {
    setFormData((prevData) => ({
      ...prevData,
      ...data
    }));
  }, []);

  const nextStep = () => {
    setStep((prevStep) => prevStep + 1);
    window.scrollTo(0, 0);
  };

  const prevStep = () => {
    setStep((prevStep) => prevStep - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend
    console.log('Form submitted with data:', formData);
    alert('Vehicle registration submitted successfully!');
    // Reset form or redirect
    setStep(1);
    setFormData({
      block: '',
      tower: '',
      flatNumber: '',
      residentName: '',
      residentType: 'owner',
      mobileNumber: '',
      email: '',
      vehicleTypes: [],
      vehicles: [],
      rfidTagNumber: '',
      emergencyContact: '',
      parkingSlotNumbers: [],
      remarks: ''
    });
  };

  // Add animation classes to Tailwind CSS
  useEffect(() => {
    const addAnimationClasses = () => {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(-100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideOutLeft {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(100%); }
        }
        .animate-slide-in {
          animation: slideInRight 0.5s ease-out forwards;
        }
        .animate-slide-out {
          animation: slideOutLeft 0.5s ease-out forwards;
        }
      `;
      document.head.appendChild(style);
    };
    
    addAnimationClasses();
  }, []);

  // Calculate progress percentage
  const totalSteps = 7;
  const progress = (step / totalSteps) * 100;
  
  // Get step name for mobile display
  const getStepName = (stepNumber) => {
    switch (stepNumber) {
      case 1: return 'Block Selection';
      case 2: return 'Flat Selection';
      case 3: return 'Resident Info';
      case 4: return 'Vehicle Type';
      case 5: return 'Vehicle Details';
      case 6: return 'Security Access';
      case 7: return 'Review & Submit';
      default: return 'Block Selection';
    }
  };

  // Render current step
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <BlockSelection
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
          />
        );
      case 2:
        return (
          <FlatSelection
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 3:
        return (
          <ResidentInfo
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 4:
        return (
          <VehicleTypeSelection
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 5:
        return (
          <VehicleDetails
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 6:
        return (
          <SecurityAccess
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 7:
        return (
          <ReviewSubmit
            formData={formData}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
          />
        );
      default:
        return <BlockSelection formData={formData} updateFormData={updateFormData} nextStep={nextStep} />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back to Home Button */}
      <div className="flex justify-start mb-6">
        <Link to="/" className="text-blue-600 hover:text-blue-800 flex items-center bg-white py-2 px-4 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Home
        </Link>
      </div>
      
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700 mb-3">Vehicle Registration</h1>
        <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full mb-4"></div>
        <p className="text-gray-600 max-w-lg mx-auto">Register your vehicle with Sapna Enclave</p>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="hidden sm:flex justify-between mt-2 text-sm text-gray-600">
          <span>Block</span>
          <span>Flat</span>
          <span>Resident</span>
          <span>Vehicle Type</span>
          <span>Details</span>
          <span>Security</span>
          <span>Review</span>
        </div>
        <div className="sm:hidden text-center mt-2 text-sm font-medium text-indigo-700">
          Step {step} of {totalSteps}: {getStepName(step)}
        </div>
      </div>
      
      {/* Step Content with Animation */}
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg p-8 mb-8 border border-blue-100">
        <div key={step} className="animate-slide-in">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default VehicleRegistrationPage;
